import { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Rect } from 'fabric';
import { createRectangle } from './RectangleTool';
import {
  GATE_ATTACH_THRESHOLD,
  GATE_DEPTH_PX,
  GATE_TYPE_ID,
  WINDOW_TYPE_ID,
  OPENING_TYPE_IDS,
  parentsForOpening,
  ensureObjectId,
  findLinkableParents,
  findNearestEdgePoint,
  clampGateT,
  pointOnEdgeAt,
  applyInheritedHeight,
} from './gateLinkUtils';
import { applyGateSymbolRendering, applyWindowSymbolRendering } from './gateSymbolRenderer';
import { getObjectTypeById } from '../../../config';
import { useCommand } from '../../../context/CommandContext';
import { CreateObjectCommand } from '../../../commands/CreateObjectCommand';

/**
 * GatePlacementTool — click placement for gates ('tor', #611)
 *
 * Replaces the old rectangle-drawing workflow for gates with a point tool:
 * - Hovering near a linkable structure (zaun/mauer/gebaeude/gewaechshaus)
 *   shows a ghost snapped onto the edge, rotated to the edge angle.
 * - Click near a structure → gate is created linked (data.link = { parentId,
 *   edgeIndex, t }), inherits angle and (heightMode 'auto') the structure
 *   height.
 * - Click into empty space → standalone gate (legacy behaviour).
 * - Escape cancels and returns to the select tool.
 *
 * Placement is undoable via CreateObjectCommand.
 */
const GatePlacementTool = ({ canvas, objectTypeId, onComplete, onCancel }) => {
  const { commandStack } = useCommand();
  const ghostRef = useRef(null);
  const snapRef = useRef(null); // last snap result (null = free placement)

  // Which opening kind the point tool places (tor | fenster, #690). Only these
  // two types use the POINT tool; anything else falls back to a gate.
  const openingTypeId = OPENING_TYPE_IDS.includes(objectTypeId) ? objectTypeId : GATE_TYPE_ID;
  const isWindow = openingTypeId === WINDOW_TYPE_ID;
  const allowedParentTypes = parentsForOpening(openingTypeId);

  // Callbacks + commandStack live in refs so the main effect does NOT depend
  // on their identity. Otherwise: ghost add fires object:added → parent
  // setState → re-render → new inline callback/commandStack identities →
  // effect cleanup removes the ghost → object:removed → setState → … endless
  // loop ("Maximum update depth exceeded") and a ghost that only survives
  // while the mouse keeps moving.
  const onCompleteRef = useRef(onComplete);
  const onCancelRef = useRef(onCancel);
  const commandStackRef = useRef(commandStack);
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onCancelRef.current = onCancel;
    commandStackRef.current = commandStack;
  });

  const gateWidthPx = getConfigDefault(openingTypeId, 'width', 100);

  const removeGhost = useCallback(() => {
    if (ghostRef.current) {
      canvas.remove(ghostRef.current);
      ghostRef.current = null;
    }
  }, [canvas]);

  const updateGhost = useCallback(
    (pointer) => {
      const snap = findNearestEdgePoint(
        pointer,
        findLinkableParents(canvas, allowedParentTypes),
        GATE_ATTACH_THRESHOLD
      );
      snapRef.current = snap;

      if (!ghostRef.current) {
        ghostRef.current = new Rect({
          originX: 'center',
          originY: 'center',
          width: gateWidthPx,
          height: GATE_DEPTH_PX,
          fill: 'rgba(16, 185, 129, 0.15)',
          stroke: '#10B981',
          strokeWidth: 2,
          strokeDashArray: [6, 4],
          selectable: false,
          evented: false,
          excludeFromExport: true,
          data: { isTemporary: true },
        });
        canvas.add(ghostRef.current);
      }

      const ghost = ghostRef.current;
      if (snap) {
        // Clamp t so the gate stays fully on the edge, then re-derive position
        const t = clampGateT(snap.t, snap.edgeLength, gateWidthPx);
        const { x, y } = pointOnEdgeAt(snap.edge, t);
        snapRef.current = { ...snap, t, x, y };
        ghost.set({
          left: x,
          top: y,
          angle: snap.angleDeg,
          stroke: '#10B981',
          fill: 'rgba(16, 185, 129, 0.15)',
        });
      } else {
        ghost.set({
          left: pointer.x,
          top: pointer.y,
          angle: 0,
          stroke: '#9CA3AF',
          fill: 'rgba(107, 114, 128, 0.1)',
        });
      }
      ghost.setCoords();
      canvas.requestRenderAll();
    },
    [canvas, gateWidthPx, allowedParentTypes]
  );

  const placeGate = useCallback(() => {
    const snap = snapRef.current;

    // A window is only meaningful seated in a wall — no free placement in the
    // air (#690). Ignore the click and keep the tool active.
    if (isWindow && !snap) return;

    // Base rect via the shared factory (garden object metadata, styles), then
    // convert to center origin so position + rotation pivot on the edge point.
    const opening = createRectangle(
      { left: 0, top: 0, width: gateWidthPx, height: GATE_DEPTH_PX },
      false,
      openingTypeId
    );

    if (isWindow) {
      // Windows carry a fixed height + sill (no auto-inherit) and render as the
      // glazing symbol instead of a door.
      opening.data = {
        ...opening.data,
        height: getConfigDefault(openingTypeId, 'height', 120),
        sillHeight: getConfigDefault(openingTypeId, 'sillHeight', 90),
        heightMode: 'custom',
      };
      applyWindowSymbolRendering(opening);
    } else {
      opening.data = {
        ...opening.data,
        hinge: 'left',
        swing: 'in',
        heightMode: 'auto',
      };
      // Render as the floor-plan door symbol instead of a plain rectangle (#611 Block B)
      applyGateSymbolRendering(opening);
    }

    if (snap) {
      const parentId = ensureObjectId(snap.parent);
      opening.data.link = {
        parentId,
        edgeIndex: snap.edgeIndex,
        t: snap.t,
      };
      opening.set({
        originX: 'center',
        originY: 'center',
        left: snap.x,
        top: snap.y,
        angle: snap.angleDeg,
      });
      // Gates inherit the parent height (auto); windows keep their own.
      if (!isWindow) applyInheritedHeight(opening, snap.parent);
    } else {
      const ghost = ghostRef.current;
      opening.set({
        originX: 'center',
        originY: 'center',
        left: ghost ? ghost.left : canvas.getWidth() / 2,
        top: ghost ? ghost.top : canvas.getHeight() / 2,
        angle: 0,
      });
    }
    opening.setCoords();

    removeGhost();

    const command = new CreateObjectCommand(canvas, opening);
    commandStackRef.current.execute(command);

    canvas.setActiveObject(opening);
    canvas.requestRenderAll();

    onCompleteRef.current?.(opening);
  }, [canvas, gateWidthPx, isWindow, openingTypeId, removeGhost]);

  // eslint-disable-next-line react-hooks/immutability -- imperative Fabric.js canvas mutations (cursor set/reset), same pattern as the drawing-tool effects in GardenCanvas
  useEffect(() => {
    if (!canvas) return undefined;

    const handleMouseMove = (e) => {
      const pointer = canvas.getScenePoint(e.e);
      updateGhost({ x: pointer.x, y: pointer.y });
    };

    const handleMouseDown = (e) => {
      const pointer = canvas.getScenePoint(e.e);
      updateGhost({ x: pointer.x, y: pointer.y });
      placeGate();
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancelRef.current?.();
      }
    };

    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:down', handleMouseDown);
    window.addEventListener('keydown', handleKeyDown);

    // eslint-disable-next-line react-hooks/immutability -- imperative Fabric.js canvas mutation (cursor)
    canvas.defaultCursor = 'crosshair';
    canvas.setCursor('crosshair');

    // Show the ghost immediately at the viewport center — the mouse position
    // is unknown until the first mouse:move (browser limitation)
    if (typeof canvas.getVpCenter === 'function') {
      const vpCenter = canvas.getVpCenter();
      updateGhost({ x: vpCenter.x, y: vpCenter.y });
    }

    return () => {
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:down', handleMouseDown);
      window.removeEventListener('keydown', handleKeyDown);
      canvas.defaultCursor = 'default';
      canvas.setCursor('default');
      removeGhost();
      canvas.requestRenderAll();
    };
  }, [canvas, updateGhost, placeGate, removeGhost]);

  // Renders nothing — purely canvas-driven
  return null;
};

/** Read an mvp property's default value from a garden-object config (px = cm). */
function getConfigDefault(typeId, key, fallback) {
  const config = getObjectTypeById(typeId);
  return config?.properties?.mvp?.[key]?.defaultValue ?? fallback;
}

GatePlacementTool.propTypes = {
  canvas: PropTypes.object.isRequired,
  /** Garden object type being placed ('tor' | 'fenster'); defaults to a gate. */
  objectTypeId: PropTypes.string,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
};

export default GatePlacementTool;
